// src/admin/pages/Dishes/DishesPage.js
import React, { useState, useEffect, useMemo } from "react";
import DishesFilterBar from "./components/dishes/DishesFilterBar";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "./DishesPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/* ---- Helper: data odierna (YYYY‑MM‑DD) nel fuso locale ------------ */
const toLocalYYYYMMDD = (d = new Date()) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

export default function DishesPage() {
  /* ------------------------------------------------------------------
     1) Filtri (default = oggi)
  ------------------------------------------------------------------ */
  const todayStr = useMemo(() => toLocalYYYYMMDD(), []);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate,   setEndDate]   = useState(todayStr);

  /* Selettori multi‑select */
  const [dishesList,         setDishesList]         = useState([]);
  const [selectedDishes,     setSelectedDishes]     = useState([]);
  const [categoriesList,     setCategoriesList]     = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  /* Ristoranti */
  const [restaurantsList, setRestaurantsList] = useState([]);
  const [restaurantId,    setRestaurantId]    = useState("all");

  /* ------------------------------------------------------------------
     2) KPI data
  ------------------------------------------------------------------ */
  const [bestDishesQuantity,     setBestDishesQuantity]     = useState([]);
  const [bestDishesRevenue,      setBestDishesRevenue]      = useState([]);
  const [bestCategoriesQuantity, setBestCategoriesQuantity] = useState([]);
  const [bestCategoriesRevenue,  setBestCategoriesRevenue]  = useState([]);
  const [menuMarkup,             setMenuMarkup]             = useState([]);

  const [analysisMode, setAnalysisMode] = useState("quantity"); // quantity | revenue
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  /* ------------------------------------------------------------------
     3) Fetch liste base (una sola volta)
  ------------------------------------------------------------------ */
  useEffect(() => {
    const token   = localStorage.getItem("access_token") || "";
    const headers = { Authorization: `Bearer ${token}` };

    const safeJSON = (res) => (res.ok ? res.json() : Promise.resolve([]));

    Promise.all([
      fetch(`${API_BASE_URL}/admin/kpi/dishes_list`,      { headers }).then(safeJSON),
      fetch(`${API_BASE_URL}/admin/kpi/categories_list`,  { headers }).then(safeJSON),
      fetch(`${API_BASE_URL}/admin/kpi/restaurants_list`, { headers }).then(safeJSON),
    ])
      .then(([dishes, cats, rests]) => {
        setDishesList(dishes);
        setCategoriesList(cats);
        setRestaurantsList(rests);
      })
      .catch((err) => console.error("Fetch liste base:", err));
  }, []);

  /* ------------------------------------------------------------------
     4) fetchAll – compone la query e scarica tutti i KPI
  ------------------------------------------------------------------ */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const token   = localStorage.getItem("access_token") || "";
      const headers = { Authorization: `Bearer ${token}` };

      /* Query string */
      const params = [];
      if (restaurantId !== "all")        params.push(`restaurant_id=${restaurantId}`);
      if (startDate)                     params.push(`start_date=${startDate}`);
      if (endDate)                       params.push(`end_date=${endDate}`);
      if (selectedDishes.length)         params.push(`dish_ids=${selectedDishes.join(",")}`);
      if (selectedCategories.length)     params.push(`categories=${selectedCategories.join(",")}`);
      const query = params.length ? `?${params.join("&")}` : "";

      /* Endpoints in parallelo */
      const endpoints = [
        ["best_dishes",                 setBestDishesQuantity],
        ["best_dishes_revenue",         setBestDishesRevenue],
        ["best_categories",             setBestCategoriesQuantity],
        ["best_categories_revenue",     setBestCategoriesRevenue],
        ["menu_markup",                 setMenuMarkup],
      ];

      await Promise.all(
        endpoints.map(async ([ep, setter]) => {
          const res = await fetch(`${API_BASE_URL}/admin/kpi/${ep}${query}`, { headers });
          if (!res.ok) throw new Error(ep);
          setter(await res.json());
        })
      );
    } catch (err) {
      console.error(err);
      setError("Impossibile caricare i dati KPI");
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------
     5) Primo caricamento KPI
  ------------------------------------------------------------------ */
  useEffect(() => {
    fetchAll(); // parte con i filtri di default (oggi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------------
     6) Handler bottone Filtra
  ------------------------------------------------------------------ */
  const handleFilter = () => fetchAll();

  /* ------------------------------------------------------------------
     7) Dataset grafici
  ------------------------------------------------------------------ */
  /* Piatti */
  const barDataSrc = analysisMode === "quantity" ? bestDishesQuantity : bestDishesRevenue;
  const barChartData = {
    labels: barDataSrc.map((x) => x.label),
    datasets: [
      {
        label: analysisMode === "quantity" ? "Ordini / Quantità" : "Fatturato",
        data:  barDataSrc.map((x) => x.value),
        backgroundColor: "#FF8C00",
      },
    ],
  };
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text:
          analysisMode === "quantity"
            ? "Piatti più venduti (Ordini)"
            : "Piatti con maggior fatturato",
      },
    },
    scales: { y: { beginAtZero: true } },
  };

  /* Categorie */
  const pieDataSrc = analysisMode === "quantity" ? bestCategoriesQuantity : bestCategoriesRevenue;
  const pieChartData = {
    labels: pieDataSrc.map((c) => c.label),
    datasets: [
      {
        data: pieDataSrc.map((c) => c.value),
        backgroundColor: ["#FF6600", "#FF8C00", "#FFB347", "#FFD27F", "#FF9933"],
      },
    ],
  };
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text:
          analysisMode === "quantity"
            ? "Categorie più vendute (Ordini)"
            : "Categorie con maggior fatturato",
      },
    },
  };

  /* Costi vs Ricavi */
  const costRevenueChartData = {
    labels: menuMarkup.map((m) => m.name),
    datasets: [
      {
        label: "Costo Totale",
        data:  menuMarkup.map((m) => m.total_cost),
        backgroundColor: "#F44336",
      },
      {
        label: "Ricavo Totale",
        data:  menuMarkup.map((m) => m.total_revenue),
        backgroundColor: "#4CAF50",
      },
    ],
  };
  const costRevenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Confronto Costi vs Ricavi" },
      datalabels: {
        color: "#fff",
        formatter: (value, ctx) => {
          const idx = ctx.dataIndex;
          const isCost = ctx.datasetIndex === 0;
          const costVal = menuMarkup[idx]?.total_cost     || 0;
          const revVal  = menuMarkup[idx]?.total_revenue  || 0;
          const total   = costVal + revVal;
          if (total === 0) return "0%";
          const pct = isCost ? (costVal / total) * 100 : (revVal / total) * 100;
          return pct.toFixed(1) + "%";
        },
      },
    },
    scales: { y: { beginAtZero: true } },
  };

  /* ------------------------------------------------------------------
     8) Render
  ------------------------------------------------------------------ */
  return (
    <div className="dishes-page">
      {/* Barra filtri */}
      <DishesFilterBar
        startDate={startDate}
        endDate={endDate}
        onChangeStart={setStartDate}
        onChangeEnd={setEndDate}
        dishesList={dishesList}
        selectedDishes={selectedDishes}
        onChangeDishes={setSelectedDishes}
        categoriesList={categoriesList}
        selectedCategories={selectedCategories}
        onChangeCategories={setSelectedCategories}
        restaurantsList={restaurantsList}
        selectedRestaurant={restaurantId}
        onChangeRestaurant={setRestaurantId}
        onFilter={handleFilter}
      />

      {loading && <p className="loading-text">Caricamento dati…</p>}
      {error   && <p className="error">{error}</p>}

      {/* Barra Ordini / Fatturato */}
      <div className="analysis-mode-bar">
        <span className="analysis-label">Visualizza per:</span>
        <button
          onClick={() => setAnalysisMode("quantity")}
          className={`analysis-btn ${analysisMode === "quantity" ? "active" : ""}`}
        >
          Ordini
        </button>
        <button
          onClick={() => setAnalysisMode("revenue")}
          className={`analysis-btn ${analysisMode === "revenue" ? "active" : ""}`}
        >
          Fatturato
        </button>
      </div>

      {/* Grafici Piatti / Categorie */}
      <div className="charts-row">
        <div className="chart-box">
          <div className="chart-container">
            {barDataSrc.length ? (
              <Bar data={barChartData} options={barChartOptions} />
            ) : (
              <p className="no-data">Nessun dato disponibile.</p>
            )}
          </div>
        </div>

        <div className="chart-box">
          <div className="chart-container">
            {pieDataSrc.length ? (
              <Pie data={pieChartData} options={pieChartOptions} />
            ) : (
              <p className="no-data">Nessun dato disponibile.</p>
            )}
          </div>
        </div>
      </div>

      {/* Grafico Costi vs Ricavi */}
      <div className="chart-box">
        <div className="chart-container larger">
          {menuMarkup.length ? (
            <Bar data={costRevenueChartData} options={costRevenueChartOptions} />
          ) : (
            <p className="no-data">Nessun dato disponibile.</p>
          )}
        </div>
      </div>
    </div>
  );
}
